import React, { useState, useEffect, useCallback } from 'react';
import { withTranslation } from '../ChildrenContext.js';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import DataComponentBody from './DataComponent__Body';
import DataComponentFilter from './DataComponent__Filter';
import DataComponentDisplayControl from './DataComponent__DisplayControl';
import DateComponentSettings from './DateComponent_Settings';
import DateComponentCurrencySettings from './DateComponent_CurrencySettings';
import Pagination from '../Pagination.js';
import query from '../../helpers/query';
import {isMobile} from '../../utils/DeviceDetect';

const DataComponent = ({
    isQuery = false,
    autoTile = false,
    headerNamesArray = false,
    loading = false,
    page,
    alignPagination = false,
    settings = false,
    fields = [],
    className = '',
    settingKey = '',
    columnData = {
        header: {},
        body: {}
    },
    onAction: onActionProp,
    onActionFilter: onActionFilterProp,
    hideResetButton,
    resetToInitial,
    commonFilterControls = false,
    gray,
    settings: settingsProp,
    headerNames,
    tableBodyTitle,
    sessionId,
    totalRows,
    data,
    currencySettings,
    currencySettingKey,
    currencySettingData,
    displayControl,
    multiLevelHeaderNames,
    totalPages,
    t,
    ...props
}) => {
    let typeDisplayControl = 'table';
    if (autoTile && isMobile && isMobile.any && isMobile.any()) {
        typeDisplayControl = 'tile';
    }

    const [state, setState] = useState({
        filters: {},
        typeDisplayControl: typeDisplayControl,
        page: page
    });

    const onAction = useCallback((type, nextFilters) => {
        const stateData = nextFilters ? { ...nextFilters } : { ...state.filters };
        onActionProp(stateData, type);
        if (isQuery) {
            query.replace(stateData);
        }
    }, [state.filters, onActionProp, isQuery]);

    const onActionFilter = useCallback((type) => {
        const stateData = {
            ...state.filters
        };

        onActionFilterProp(stateData, type);
    }, [state.filters, onActionFilterProp]);

    const onPageChange = useCallback((page) => {
        const nextFilters = {
            ...state.filters,
            page
        };

        setState(prevState => ({
            ...prevState,
            filters: nextFilters
        }));

        onAction('pageChange', nextFilters);
    }, [state.filters, onAction]);

    const onFiltersApply = useCallback((stateData, type = 'filtersChange') => {
        if (typeof onActionFilterProp !== 'undefined') {
            onActionFilterProp(stateData, type);
        } else {
            onActionProp(stateData, type);
        }

        if (isQuery) {
            query.replace(stateData);
        }

        setState(prevState => ({
            ...prevState,
            filters: { ...stateData }
        }));
    }, [onActionFilterProp, onActionProp, isQuery]);

    const onDisplayControlChange = useCallback((type) => {
        setState(prevState => ({
            ...prevState,
            typeDisplayControl: type
        }));
    }, []);

    const getClassFooter = useCallback(() => {
        return classNames({
            'ibox-footer': true,
            'text-right': alignPagination
        });
    }, [alignPagination]);

    useEffect(() => {
        if (page !== state.page) {
            setState(prevState => ({
                ...prevState,
                page: page || prevState.page
            }));
        }
    }, [page]);

    useEffect(() => {
        if (!fields.length) {
            onActionProp && onActionProp({}, 'firstRender');
        }
    }, [fields.length, onActionProp]);

    return (
        <div className="ibox">
            {fields.length
                ? (
                    <DataComponentFilter
                        fields={fields}
                        loading={loading}
                        onAction={onFiltersApply}
                        hideResetButton={hideResetButton}
                        resetToInitial={resetToInitial}
                        commonControls={commonFilterControls}
                        gray={gray}
                    />
                )
                : null
            }
            <DateComponentSettings
                settings={settingsProp}
                headerNames={headerNames}
                tableBodyTitle={tableBodyTitle}
                settingKey={settingKey}
                loading={loading}
                sessionId={sessionId}
                totalRows={totalRows}
                data={data}
                filters={state.filters}
            />
            <DateComponentCurrencySettings
                settings={currencySettings}
                headerNames={headerNames}
                tableBodyTitle={tableBodyTitle}
                settingKey={currencySettingKey}
                loading={loading}
                data={currencySettingData}
                filters={state.filters}
            />
            <div className="ibox-content">
                {displayControl
                    ? <DataComponentDisplayControl
                        data={displayControl.data}
                        type={state.typeDisplayControl}
                        name={displayControl.name}
                        onChange={onDisplayControlChange}
                      />
                    : null
                }
                <DataComponentBody
                    data={data}
                    headerNames={headerNames}
                    headerNamesArray={headerNamesArray}
                    autoTile={autoTile}
                    multiLevelHeaderNames={multiLevelHeaderNames}
                    columnData={columnData}
                    loading={loading}
                    settings={settingsProp}
                    settingKey={settingKey}
                    displayControl={{
                        ...displayControl,
                        type: state.typeDisplayControl
                    }}
                    filters={state.filters}
                    className={className}
                />
            </div>
            {totalPages > 1
                ? <div className={getClassFooter()}>
                    <Pagination
                        onPageChange={onPageChange}
                        initialPage={state.filters.page}
                        totalPages={totalPages}
                        loading={loading}
                        align={alignPagination ? 'right' : 'left'}
                    />
                  </div>
                : null
            }
        </div>
    );
};

DataComponent.propTypes = {
    isQuery: PropTypes.bool,
    autoTile: PropTypes.bool,
    headerNamesArray: PropTypes.bool,
    loading: PropTypes.bool,
    page: PropTypes.number,
    alignPagination: PropTypes.bool,
    settings: PropTypes.bool,
    fields: PropTypes.array,
    className: PropTypes.string,
    settingKey: PropTypes.string,
    columnData: PropTypes.shape({
        header: PropTypes.object,
        body: PropTypes.object
    }),
    onAction: PropTypes.func,
    onActionFilter: PropTypes.func,
    hideResetButton: PropTypes.bool,
    resetToInitial: PropTypes.bool,
    commonFilterControls: PropTypes.bool,
    gray: PropTypes.bool,
    headerNames: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    tableBodyTitle: PropTypes.string,
    sessionId: PropTypes.string,
    totalRows: PropTypes.number,
    data: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    currencySettings: PropTypes.bool,
    currencySettingKey: PropTypes.string,
    currencySettingData: PropTypes.object,
    displayControl: PropTypes.shape({
        type: PropTypes.string,
        name: PropTypes.string,
        data: PropTypes.array
    }),
    multiLevelHeaderNames: PropTypes.object,
    totalPages: PropTypes.number,
    t: PropTypes.object
};

export default withTranslation(DataComponent);
